import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { postData } from '../../../utility/Utility';
import Swal from 'sweetalert2';
import CustomLoader from '../../../Components/CustomLoader/CustomLoader';

export default function IpManagementlist() {
    const navigate = useNavigate();
    const [ips, setIps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const rowsPerPage = 10;

    const fetchIps = useCallback(async () => {
        setLoading(true);
        try {
            const response = await postData('api/ip-management/list', {
                status: statusFilter,
                page: currentPage + 1,
                limit: rowsPerPage
            });
            setIps(response.data.ips);
            setPageCount(response.data.totalPages);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, currentPage, rowsPerPage]);

    useEffect(() => {
        fetchIps();
    }, [fetchIps]);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    const handleDelete = async (id) => {

        setLoading(true);
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will soft delete the IP record',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await postData('api/ip-management/update', {
                    id: id,
                    isDeleted: true
                });
                if (response.success) {
                    toast.success('IP soft deleted successfully');
                    fetchIps();
                } else {
                    throw new Error(response?.message || 'Failed to soft delete IP');
                }
            } catch (error) {
                toast.error(error.message || 'Error soft deleting IP');
            }
        }
    };

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            const response = await postData('api/ip-management/update', {
                id: id,
                status: newStatus
            });

            if (response.success) {
                fetchIps();
            } else {
                throw new Error(response?.message || 'Failed to update status');
            }
        } catch (error) {
            toast.error(error.message);
                }
    };

    return (
        <div className="content-page">
            <CustomLoader loading={loading} />
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="content">
                <div className="container-fluid">
                    <div className="py-3 d-flex justify-content-between">
                        <h4 className="fs-18 fw-semibold m-0">IP Management</h4>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/add-ip')}
                            disabled={loading}
                        >
                            Add IP
                        </button>
                    </div>

                    <div className="breadcrumb">
                        <Link to="/home">Home</Link>
                        <span> / </span>
                        <Link to="/ip-list">IP Management</Link>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row g-3 align-items-center mb-3">
                                        <div className="col-md-4">
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                <option value="all">All Statuses</option>
                                                <option value="active">Active</option>
                                                <option value="blocked">Blocked</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        {ips.length > 0 ? (
                                            <table className="table table-hover ip-list-table">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>IP Address</th>
                                                        <th>Title</th>
                                                        <th data-type="status">Status</th>
                                                        <th className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ips.map((ip, index) => (
                                                        <tr key={ip._id}>
                                                            <td>{index + 1 + (currentPage * rowsPerPage)}</td>
                                                            <td data-type="ip">
                                                                <div>{ip.ipAddress}</div>
                                                                <small className="text-muted">
                                                                    {ip.description?.split('\n')[0] || 'No description'}
                                                                </small>
                                                            </td>
                                                            <td>{ip.title}</td>
                                                            <td data-type="status">
                                                                <span className={`text-capitalize badge ${ip.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                                    {ip.status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2 justify-content-center">
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => navigate(`/add-ip`, { state: ip })}
                                                                        title="Edit IP"
                                                                    >
                                                                        <i className="fas fa-edit me-1"></i>
                                                                    </button>
                                                                    {ip.status === 'active' ? (
                                                                        <button
                                                                            className="btn btn-sm btn-outline-warning"
                                                                            onClick={() => handleStatusUpdate(ip._id, 'blocked')}
                                                                            title="Block IP"
                                                                        >
                                                                            <i className="fas fa-ban me-1"></i>
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            className="btn btn-sm btn-outline-success"
                                                                            onClick={() => handleStatusUpdate(ip._id, 'active')}
                                                                            title="Unblock IP"
                                                                        >
                                                                            <i className="fas fa-check me-1"></i>
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => handleDelete(ip._id)}
                                                                        title="Delete IP"
                                                                    >
                                                                        <i className="fas fa-trash me-1"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="fas fa-network-wired fa-3x text-muted mb-3"></i>
                                                <h4>No IPs Found</h4>
                                                <p>Get started by adding your first IP address</p>
                                                <button 
                                                    className="btn btn-primary"
                                                    onClick={() => navigate('/add-ip')}
                                                >
                                                    <i className="fas fa-plus me-2"></i>Add IP
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <ReactPaginate
                                        previousLabel={<i className="mdi mdi-chevron-left"></i>}
                                        nextLabel={<i className="mdi mdi-chevron-right"></i>}
                                        breakLabel="..."
                                        breakClassName="page-item"
                                        breakLinkClassName="page-link"
                                        pageCount={pageCount}
                                        marginPagesDisplayed={2}
                                        pageRangeDisplayed={5}
                                        onPageChange={handlePageChange}
                                        containerClassName="pagination pagination-rounded justify-content-end mt-3"
                                        pageClassName="page-item"
                                        pageLinkClassName="page-link"
                                        previousClassName="page-item"
                                        previousLinkClassName="page-link"
                                        nextClassName="page-item"
                                        nextLinkClassName="page-link"
                                        activeClassName="active"
                                        forcePage={currentPage}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
